import { FC } from 'react';
import aiAvatar from '@/assets/images/ai-avatar.png';
import { AIChatMessage } from '@/types/api/aiChat';

/**
 * AIが提示した選択肢を本文から切り出す
 *
 * エージェントは末尾に「[確認してもらう] [今は大丈夫]」の形式で選択肢を並べる。
 * これをタップできるショートカットにして、利用者が入力せずに1タップで返せるようにする。
 *
 * 本文の途中に出てくる括弧を誤って拾わないよう、末尾の連続した [...] だけを対象にする。
 */
export const extractChoices = (
  text: string,
): { body: string; choices: string[] } => {
  // 末尾の「[...] [...]」の並び（間の空白・改行は許容）
  const trailing = /(?:\s*\[[^[\]\n]{1,30}\])+\s*$/.exec(text);

  if (!trailing) {
    return { body: text, choices: [] };
  }

  const choices = Array.from(trailing[0].matchAll(/\[([^[\]\n]{1,30})\]/g)).map(
    (m) => m[1].trim(),
  );

  // 1つだけの場合は選択肢ではなく強調表現の可能性があるため、2つ以上を条件にする
  if (choices.length < 2) {
    return { body: text, choices: [] };
  }

  return {
    body: text.slice(0, trailing.index).trimEnd(),
    choices,
  };
};

interface ChatMessageProps {
  message: AIChatMessage;
  /** 選択肢のタップで送信する。最新のAI発言にだけ渡す */
  onSelectChoice?: (choice: string) => void;
  /** 送信中は選択肢を押せないようにする */
  disabled?: boolean;
}

const styles = {
  container: (isUser: boolean): React.CSSProperties => ({
    display: 'flex',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    marginBottom: '12px',
    // 相手側に余白を残すことで、長文でも画面幅いっぱいにならない
    paddingLeft: isUser ? '20%' : '0',
    paddingRight: isUser ? '0' : '12%',
  }),
  // 発言の塊。幅の上限はここで持つ。
  // 吹き出し側に maxWidth: 80% を置くと、長文のときだけ
  // 「伸縮した親の80%」になって右端が内側にずれる。
  group: (isUser: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: isUser ? 'flex-end' : 'flex-start',
    maxWidth: '100%',
    minWidth: 0,
  }),
  bubble: (isUser: boolean): React.CSSProperties => ({
    padding: '12px 16px',
    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    backgroundColor: isUser ? '#1E55E6' : '#F0F0F5',
    color: isUser ? '#FFFFFF' : '#333333',
    fontSize: '14px',
    lineHeight: '1.5',
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  }),
  time: {
    fontSize: '11px',
    color: '#999',
    marginTop: '4px',
    textAlign: 'right' as const,
  },
  // ダッシュボード（CarInfoCard）と同じ ai-avatar.png を使う
  aiIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    marginRight: '8px',
    flexShrink: 0,
    objectFit: 'cover' as const,
  },
  messageWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    maxWidth: '100%',
    // flex の子は既定で縮まないため、これが無いと長文が折り返さない
    minWidth: 0,
  },
  choices: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginTop: '8px',
  },
  choice: (disabled: boolean): React.CSSProperties => ({
    padding: '8px 14px',
    borderRadius: '16px',
    border: '1px solid #1E55E6',
    backgroundColor: '#FFFFFF',
    color: '#1E55E6',
    fontSize: '13px',
    fontWeight: 'bold',
    lineHeight: 1.2,
    opacity: disabled ? 0.5 : 1,
  }),
};

const AiAvatar: FC = () => <img src={aiAvatar} alt="" style={styles.aiIcon} />;

export const ChatMessage: FC<ChatMessageProps> = ({
  message,
  onSelectChoice,
  disabled = false,
}) => {
  const isUser = message.role === 'user';

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 選択肢を出すのは、タップで送信できる最新のAI発言のときだけ
  const { body, choices } = onSelectChoice
    ? extractChoices(message.message)
    : { body: message.message, choices: [] };

  return (
    <div style={styles.container(isUser)}>
      {!isUser && (
        <div style={styles.messageWrapper}>
          <AiAvatar />
          <div style={styles.group(false)}>
            <div style={styles.bubble(false)}>{body}</div>
            {choices.length > 0 && (
              <div style={styles.choices}>
                {choices.map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    style={styles.choice(disabled)}
                    disabled={disabled}
                    onClick={() => onSelectChoice?.(choice)}
                  >
                    {choice}
                  </button>
                ))}
              </div>
            )}
            <div style={styles.time}>{formatTime(message.created_at)}</div>
          </div>
        </div>
      )}
      {isUser && (
        <div style={styles.group(true)}>
          <div style={styles.bubble(true)}>{message.message}</div>
          <div style={styles.time}>{formatTime(message.created_at)}</div>
        </div>
      )}
    </div>
  );
};

interface StreamingMessageProps {
  content: string;
}

export const StreamingMessage: FC<StreamingMessageProps> = ({ content }) => {
  return (
    <div style={styles.container(false)}>
      <div style={styles.messageWrapper}>
        <AiAvatar />
        <div style={styles.group(false)}>
          <div style={styles.bubble(false)}>
            {content}
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '16px',
                backgroundColor: '#666',
                marginLeft: '2px',
                animation: 'blink 1s infinite',
              }}
            />
          </div>
        </div>
      </div>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};
